import { useAutoAnimate } from "@formkit/auto-animate/react";
import {
  AppSettingsNavigationDTO,
  ClubClassesSettingsDTO,
} from "@gymflow/types";
import { useQueryAppClassesSettings } from "libs/api/src/lib/hooks/club/useQueryAppClassesSettings";
import { useQueryAppNavigationSettings } from "libs/api/src/lib/hooks/club/useQueryAppNavigationSettings";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Route } from "react-router";

import { usePortalRoutes } from "../../../hooks";
import { useClubSettings } from "../../../providers";
import { RouteFeature, RouteLayout } from "../../../routes";
import useGymflowModels from "../../../store";
import { Spinner } from "../../atoms";
import PillTabsLayout from "../../molecules/PillTabsLayout";
import { SettingsRoute } from "../SettingsRoute";
import { AppClassPreview } from "./AppClassPreview";
import { AppSettingsClasses } from "./AppSettingsClasses";
import { AppSettingsContext } from "./AppSettingsContext";
import { AppSettingsNavigation } from "./AppSettingsNavigation";
import { AppSettingsPreview } from "./AppSettingsPreview";

export function AppSettings() {
  const { t } = useTranslation();
  const { createClubLink } = usePortalRoutes();
  const { api } = useGymflowModels();
  const { clubId } = useClubSettings();
  const appNavigationQuery = useQueryAppNavigationSettings({ api, clubId });
  const [navigationLocalData, setNavigationLocalData] =
    useState<AppSettingsNavigationDTO>();
  useEffect(() => {
    if (appNavigationQuery.data && appNavigationQuery.isSuccess) {
      setNavigationLocalData(appNavigationQuery.data);
    }
  }, [appNavigationQuery.data, appNavigationQuery.isSuccess]);

  const appClassesQuery = useQueryAppClassesSettings({ api, clubId });
  const [classesLocalData, setClassesLocalData] =
    useState<ClubClassesSettingsDTO>();
  useEffect(() => {
    if (appClassesQuery.data && appClassesQuery.isSuccess) {
      setClassesLocalData(appClassesQuery.data);
    }
  }, [appClassesQuery.data, appClassesQuery.isSuccess]);
  const [parent] = useAutoAnimate();
  return (
    <AppSettingsContext.Provider
      value={{
        navigationLocalData,
        setNavigationLocalData,
        classesLocalData,
        setClassesLocalData,
      }}
    >
      <div className="relative flex size-full">
        <div className="absolute -inset-8 top-0 flex flex-col gap-4 overflow-auto border-t border-gray-200 bg-white p-8 lg:flex-row">
          <div className="flex w-full gap-6 max-lg:flex-col lg:flex-row">
            <div className="flex rounded-3xl border border-gray-200 p-6 max-lg:w-full lg:w-2/3">
              {appNavigationQuery.isLoading && <Spinner />}
              {!appNavigationQuery.isLoading && navigationLocalData && (
                <PillTabsLayout
                  variant="secondary"
                  tabs={[
                    {
                      text: t(
                        "page.settings.appSettings.tabs.navigation.navBarTitle",
                      ),
                      path: createClubLink(
                        RouteLayout.Staff,
                        RouteFeature.Settings,
                        SettingsRoute.AppSettings,
                        "navigation",
                      ),
                      component: AppSettingsNavigation,
                    },

                    {
                      text: t(
                        "page.settings.appSettings.tabs.classes.navBarTitle",
                      ),
                      path: createClubLink(
                        RouteLayout.Staff,
                        RouteFeature.Settings,
                        SettingsRoute.AppSettings,
                        "classes",
                      ),
                      component: AppSettingsClasses,
                    },
                  ]}
                  enableAnimations
                />
              )}
            </div>
            <div ref={parent} className="flex w-1/3 min-w-96 max-lg:hidden">
              {appNavigationQuery.isLoading && <Spinner />}
              <Route
                path={createClubLink(
                  RouteLayout.Staff,
                  RouteFeature.Settings,
                  SettingsRoute.AppSettings,
                  "classes",
                )}
              >
                {!appNavigationQuery.isLoading && <AppClassPreview />}
              </Route>
              <Route
                path={createClubLink(
                  RouteLayout.Staff,
                  RouteFeature.Settings,
                  SettingsRoute.AppSettings,
                  "navigation",
                )}
              >
                {!appNavigationQuery.isLoading && (
                  <AppSettingsPreview imageClassName="max-h-[65vh]" />
                )}
              </Route>
            </div>
          </div>
        </div>
      </div>
    </AppSettingsContext.Provider>
  );
}
